import axios, { AxiosError } from "axios";
import { useCallback, useContext } from "react";
import useSWR from "swr";
import { AuthContext } from "../../../contexts/AuthContext";
import { APIEndpointEnum } from "../../../enums/endpoint";
import { apiFetcher } from "../../../services/api/apiFetcher";
import { API_URL } from "../../../utils/env";

export interface IReferralCode {
  code: string;
  label: string | null;
  created_at: number;
}

interface IReferralCodesResponse {
  codes: IReferralCode[];
}

const authHeaders = (apiKey?: string, apiSecret?: string) =>
  apiKey && apiSecret
    ? { "API-KEY": apiKey, "API-SECRET": apiSecret }
    : undefined;

/**
 * List + create + delete the connected account's referral codes. A user may
 * own multiple codes (the referrals-service is the source of truth). The
 * backend gateway authenticates via API-KEY/SECRET and forwards the account.
 */
export const useReferralCodes = () => {
  const { apiKey, apiSecret } = useContext(AuthContext);

  const swr = useSWR<IReferralCodesResponse, AxiosError>(
    apiKey && apiSecret ? [APIEndpointEnum.REFERRAL_CODES, apiKey] : undefined,
    async () =>
      apiFetcher<IReferralCodesResponse>({
        url: APIEndpointEnum.REFERRAL_CODES,
        method: "GET",
        apiKey,
        apiSecret,
      })
  );

  // Create a code. `code` optional (slug auto-generated if omitted); `label`
  // optional. Returns the created code string. Surfaces the backend's error
  // message (e.g. "code already taken", validation) so the UI can show it.
  const createCode = useCallback(
    async (code?: string, label?: string): Promise<string> => {
      try {
        const { data } = await axios.request<{ created: string }>({
          url: `${API_URL}/${APIEndpointEnum.REFERRAL_CODES.replace(/^\//, "")}`,
          method: "POST",
          headers: authHeaders(apiKey, apiSecret),
          data: { code: code || undefined, label: label || undefined },
        });
        await swr.mutate();
        return data.created;
      } catch (error) {
        const e = error as AxiosError<{ error?: string }>;
        throw new Error(e.response?.data?.error || "Failed to create code");
      }
    },
    [apiKey, apiSecret, swr]
  );

  const deleteCode = useCallback(
    async (code: string): Promise<void> => {
      try {
        await axios.request({
          url: `${API_URL}/${APIEndpointEnum.REFERRAL_CODES.replace(/^\//, "")}/${encodeURIComponent(code)}`,
          method: "DELETE",
          headers: authHeaders(apiKey, apiSecret),
        });
        await swr.mutate();
      } catch (error) {
        const e = error as AxiosError<{ error?: string }>;
        throw new Error(e.response?.data?.error || "Failed to delete code");
      }
    },
    [apiKey, apiSecret, swr]
  );

  return { ...swr, codes: swr.data?.codes ?? [], createCode, deleteCode };
};
